import { Component, input, OnInit, inject } from '@angular/core';
import { QuestionItem } from '../../feedback-types';
import { ControlContainer, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CheckboxArrayControl } from './feedback-check-block-control';
import { wildcardId } from '../../../../utils/feedback/feedback';

@Component({
  selector: 'app-feedback-check-block',
  imports: [ReactiveFormsModule],
  templateUrl: './feedback-check-block.component.html',
  styleUrl: './feedback-check-block.component.css',
})
export class FeedbackCheckBlockComponent implements OnInit {
  private controlContainer = inject(ControlContainer);

  public question = input.required<QuestionItem>();
  protected form!: FormGroup;
  protected additionalInfo = '';
  private lastAddedOption = '';
  private validators: ValidatorFn[] = [];
  protected checked = false;
  protected formControlName = '';

  public ngOnInit() {
    this.form = this.controlContainer.control as FormGroup;
    this.formControlName = this.question().id;
    this.form.addControl(this.formControlName, new CheckboxArrayControl());
    const q = this.question();
    this.validators = q.validators || [];
    this.form.get(this.formControlName)?.setValidators(this.validators);
    if (q.wildcard) {
      // Find any value that starts with the wildcard id
      const wildcardValue = this.form
        .get(this.formControlName)
        ?.value.find((val: string) => val.startsWith(wildcardId));

      if (wildcardValue) {
        this.checked = true;
        this.additionalInfo = wildcardValue.replace(wildcardId, '');
        this.updateControlValue();
      }
    }
  }

  protected getErrors(): string[] {
    const errors = this.form.get(this.formControlName)?.errors;
    if (!errors) {
      return [];
    }

    const errorMessages: { [key: string]: string } = {
      required: 'This field is required.',
      maxArrayLength: `Maximum of ${errors['maxLength']} selections allowed. Please adjust your choices.`,
      minArrayLength: `Minimum of ${errors['minLength']} selections required. Please adjust your choices.`,
      emptyWildcard: ' ', // Custom error message
    };

    return Object.keys(errors).map((errorKey) => errorMessages[errorKey] || 'Unknown error: ' + errorKey);
  }

  protected updateAdditionalInfo(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.additionalInfo = inputElement.value;
    this.removeControlValue();
    this.checked = true;
    this.updateControlValue();
  }

  protected toggleOption(option: string) {
    const control = this.form.get(this.formControlName) as CheckboxArrayControl;
    control.toggleOption(option);
  }

  protected toggleWildcardOption(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.checked = true;
      this.updateControlValue();
    } else {
      this.checked = false;
      this.removeControlValue();
    }
  }

  private removeControlValue() {
    const control = this.form.get(this.formControlName) as CheckboxArrayControl;
    control.removeOption(this.lastAddedOption);
    control.setValidators(this.validators);
    control.updateValueAndValidity();
  }

  private updateControlValue() {
    const control = this.form.get(this.formControlName) as CheckboxArrayControl;
    const value = wildcardId + this.additionalInfo;
    this.lastAddedOption = value;
    control.addOption(value);
    control.addValidators(this.customValidator.bind(this));
    control.updateValueAndValidity();
  }

  private customValidator(): ValidationErrors | null {
    const value = wildcardId + this.lastAddedOption;
    return value === wildcardId ? { emptyWildcard: true } : null;
  }
}
